import React, { useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { checkMarketplaceCurrentUser } from '../../util/data';
import { decodeLatLngBounds, encodeLatLngBounds, parse } from '../../util/urlHelpers';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { SearchMap, Page, LayoutWrapperTopbar } from '../../components';
import { TopbarContainer } from '../../containers';
import { updateUserProfileInfo } from '../../ducks/user.duck';
import {
    setMapBounds,
    searchListings,
    searchMapListings,
    combineSearchMapListings,
    discardVisitedListings,
    setListingScores,
} from './SearchPage.duck';
import {
    pickSearchParamsOnly,
    validURLParamsForExtendedData,
    validFilterParams,
    createSearchResultSchema,
    getFilters,
    applyVerticalSpacing,
    defaultSearchParams,
} from './SearchPage.helpers';
import MainPanel from './MainPanel';
import css from './SearchPage.css';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { selectOwnRiderListings } from '../../ducks/riderListings.duck';
import { getMatchingScoreBulk } from '../../util/api';
import { resolveStatusByMatchingMatrixScore, SCORE_INSUFFICIENT } from '../../util/user';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 40;
const MOBILE_BREAKPOINT = 1024; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 1000; // Little bit of debounce before search is initiated.

const { listingTypeHorse, listingTypeRider, userTypeRider, userTypeHorseowner } = config;

const SearchPageComponent = ({
    history,
    location,
    params,
    currentUser,
    onUpdateUserProfileInfo,
    intl,
    searchMapListingIdsStr,
    scrollingDisabled: scrollingDisabledProp,
    updateInfoInProgress,
    updateInfoError,
    onSearchListings,
    onSearchMapListings,
    onCombineSearchMapListings,
    onDiscardingVisitedListings,
    activeListingId,
    mapBoundsString,
    onMapBoundsSetting,
    onSettingListingScoresBunch,
    ...rest
}) => {
    const [mapFullScreenViewed, setMapFullScreenViewed] = useState(false);
    const [mapPanelOpen, setMapPanelOpen] = useState(false);
    const [isMobileLayout, , computing] = useIsMobile(MOBILE_BREAKPOINT);
    const [mapVerticalSpacingAdjusted, setMapVerticalSpacingAdjusted] = useState(false);
    const [riderListing, setRiderListing] = useState(null);
    /** what the bounds-driven effect last dispatched, so identical input is not re-sent */
    const lastMapListingsSignature = useRef(null);
    const lastListingsSearchSignature = useRef(null);
    /** matching scores already fetched in this session, keyed by listing uuid */
    const listingScoresCache = useRef({});
    const listingScoresSubject = useRef(null);

    const locationSearch = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
    });
    /**
     * isLandingPage - the page is rendered inside lp in iframe,
     * no need to show top bar and map
     */
    const {
        page,
        mobilesearch,
        distance,
        mapView: mapViewQueryParam,
        isLandingPage,
        ...searchInURL
    } = locationSearch;
    const { listingType, sort, extraSort, address, bounds, origin, ...otherParamsAndFilters } =
        searchInURL || {};
    const { mapSearch, ...filtersSearchParams } = otherParamsAndFilters || {};

    const mapViewMaybe = mapViewQueryParam ? { mapView: true } : {};
    const distanceMaybe = distance ? { distance } : {};

    const filtersSearchParamsString = JSON.stringify(filtersSearchParams || {});
    /**
     * Panning the map emits a new bounds string per `idle` event. Debouncing here,
     * rather than at the map, means every consumer below sees one settled viewport
     * per gesture instead of one per frame of the pan.
     */
    const debouncedMapBoundsString = useDebouncedValue(mapBoundsString, SEARCH_WITH_MAP_DEBOUNCE);

    const currentUserId = currentUser && currentUser.id && currentUser.id.uuid;
    const currentUserType = currentUserId
        ? currentUser.attributes.profile.publicData.userType
        : null;
    const mainHorseId = currentUserId
        ? currentUser.attributes.profile.publicData.mainHorseId
        : null;

    const isRider = currentUserType === userTypeRider;
    const isHorseOwner = currentUserType === userTypeHorseowner;
    const isHorsesSearch = listingType === listingTypeHorse;
    const isRidersSearch = listingType === listingTypeRider;
    /**
     * a horseowner looks on a horse listing;
     * not allowed, edge case is handled
     */
    const ownersView = isHorseOwner && isHorsesSearch;
    /**
     * no matching
     * - when rider searches for other riders
     * - when owner searches for other horses
     */
    const riderViewsRider = isRider && isRidersSearch;

    const matchingAllowed = Boolean(
        currentUserType &&
            !riderViewsRider &&
            !ownersView &&
            (isRider ? riderListing : true) &&
            (isHorseOwner ? mainHorseId : true) &&
            mapPanelOpen
    );

    /**
     * the current user's own rider listing, used below for matching - fetched
     * once per session by middlewares/currentUser.js, read here rather than
     * re-fetched (see riderListings.duck.js:selectOwnRiderListings)
     */
    const [riderListings] = useSelector(state => selectOwnRiderListings(state, currentUserId));

    const handleListingsSearch = (params, search) => {
        onSearchListings(params, search)
            .then(response => {
                const { status, data } = response;
                const isSuccessfulResp = status === 200 && data && data.data;

                if (isSuccessfulResp /* && !mapView */) {
                    onCombineSearchMapListings();
                }
            })
            .catch(() => {
                // do nothing
            });
    };

    useEffect(() => {
        if (Array.isArray(riderListings)) {
            setRiderListing(
                riderListings.find(
                    ({
                        attributes: {
                            publicData: { userRepresentationId },
                        },
                    }) => userRepresentationId === currentUserId
                )
            );
        }
    }, [riderListings, currentUserId]);

    /**
     * Every score on the page is calculated against a single subject: the current
     * user's rider listing, or their main horse. A new subject invalidates the lot.
     */
    const riderListingId = riderListing && riderListing.id && riderListing.id.uuid;
    const matchingSubjectId = isRider ? riderListingId : mainHorseId;

    useEffect(() => {
        if (!matchingAllowed) return;
        if (!searchMapListingIdsStr) return;
        if (!matchingSubjectId) return;

        if (listingScoresSubject.current !== matchingSubjectId) {
            listingScoresSubject.current = matchingSubjectId;
            listingScoresCache.current = {};
        }

        const scoredListings = listingScoresCache.current;
        const listingIds = searchMapListingIdsStr.split(',').filter(id => !scoredListings[id]);

        /**
         * This effect runs twice when the map panel opens, because
         * `searchMapListingIdsStr` empties and then refills. Ids scored on the first
         * run are served from the cache on the second, so the repeat costs nothing.
         */
        if (listingIds.length === 0) return;

        getMatchingScoreBulk({
            ...(isRider ? { riderListingId } : { mainHorseId }),
            listingIds,
        })
            .then(scores => {
                if (!Array.isArray(scores)) return;

                scores.forEach(({ listingId, matchingScore }) => {
                    scoredListings[listingId] =
                        matchingScore && matchingScore.finalScore
                            ? resolveStatusByMatchingMatrixScore(matchingScore.finalScore)
                            : SCORE_INSUFFICIENT;
                });

                onSettingListingScoresBunch({ ...scoredListings });
            })
            .catch(() => {
                // do nothing
            });
    }, [matchingAllowed, searchMapListingIdsStr, matchingSubjectId]);

    useEffect(() => {
        if (!isLandingPage) return;

        handleListingsSearch({ bounds }, location.search);
    }, [isLandingPage]);

    useEffect(() => {
        // discard visited listings on a new session
        onDiscardingVisitedListings();
        /** This block breaks listingType switching upon navigating to LP and back to SP */
        // return () => {
        //     onMapBoundsSetting(null);
        //     // TODO discard impressions on the page leave
        // };
    }, []);

    useEffect(() => {
        // scroll to active listing, e.g. when a user comes from LP
        if (!activeListingId) return;
        if (typeof isMobileLayout === 'undefined') return;

        setTimeout(() => {
            const listingCardNode = document.getElementById(`ListingCard-${activeListingId}`);
            listingCardNode &&
                listingCardNode.scrollIntoView(
                    isMobileLayout ? { block: 'end' } : { block: 'end', behavior: 'smooth' }
                );
        }, 500);
    }, [isMobileLayout]);

    useEffect(() => {
        setMapPanelOpen(!!mapViewQueryParam);
    }, [mapViewQueryParam]);
    /**
     * This block breaks pagination
     * since the mapBoundsString is nul upon changing page
     */
    // useEffect(() => {
    //     /**
    //      * skip use effect on the mount phase
    //      */
    //     if (computing) return;
    //     /**
    //      * remove bounds on page navigation,
    //      * e.g. when back button is clicked
    //      */
    //     onMapBoundsSetting(null);
    // }, [address, searchBoundsString]);

    useEffect(() => {
        onMapBoundsSetting(bounds);
    }, [address, bounds]);

    /**
     * Single entry point for every bounds-driven request.
     *
     * The two requests it drives react to different inputs - the map listings only
     * care about the viewport, the panel state and the listing type, while the result
     * list also reacts to pagination, sorting and filters - so each keeps its own
     * signature and is dispatched only when that signature actually changed.
     */
    useEffect(() => {
        if (!debouncedMapBoundsString) return;

        const decodedBounds = decodeLatLngBounds(debouncedMapBoundsString);

        const mapListingsSignature = JSON.stringify([
            debouncedMapBoundsString,
            mapPanelOpen,
            listingType,
        ]);

        if (lastMapListingsSignature.current !== mapListingsSignature) {
            lastMapListingsSignature.current = mapListingsSignature;

            if (mapPanelOpen) {
                onSearchMapListings({ bounds: decodedBounds }, location.search);
            } else {
                onCombineSearchMapListings();
            }
        }

        /**
         * The result list is requested with the url bounds whenever the url has them, and with
         * the map viewport only when it has none (a bare /s):
         * - the url changes at once, the map bounds only after the debounce, so a map move
         *   (which also pushes page=1) re-ran this effect with the new url and the old bounds;
         * - after an address search the map is fitted to the url bounds and reports a wider
         *   viewport, so every load sent the list twice, first with the url bounds, then with
         *   the viewport, and the two kept overwriting each other's session cache entry.
         * With the url bounds, the url and the bounds always come from the same location, and
         * the viewport catching up leaves the signature unchanged.
         */
        const listingsBoundsString = bounds ? encodeLatLngBounds(bounds) : debouncedMapBoundsString;

        const listingsSearchSignature = JSON.stringify([
            listingsBoundsString,
            mapPanelOpen,
            location.search,
        ]);

        if (lastListingsSearchSignature.current !== listingsSearchSignature) {
            lastListingsSearchSignature.current = listingsSearchSignature;

            handleListingsSearch(
                { bounds: decodeLatLngBounds(listingsBoundsString) },
                location.search
            );
        }
    }, [
        mapPanelOpen,
        page,
        debouncedMapBoundsString,
        listingType,
        sort,
        extraSort,
        filtersSearchParamsString,
    ]);

    const filters = getFilters({ intl, ...rest }, searchInURL);

    const navigateToSearchPage = (params = {}) => {
        const searchParams = {
            page,
            distance,
            ...searchInURL,
            ...validFilterParams(rest, filters),
            ...params,
        };

        history.push(
            createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams)
        );
    };

    // Callback to determine if new search is needed
    // when map is moved by user or viewport has changed
    const onMapMoveEnd = (viewportBoundsChanged, data) => {
        const { viewportBounds, viewportMapCenter: viewportCenter } = data;
        const searchPagePath = pathByRouteName('SearchPage', routeConfiguration());
        const currentPath =
            typeof window !== 'undefined' && window.location && window.location.pathname;

        // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
        const isSearchPage = currentPath === searchPagePath;
        // If mapSearch url param is given
        // or original location search is rendered once,
        // we start to react to "mapmoveend" events by generating new searches
        // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)

        if (viewportBoundsChanged && isSearchPage && !computing && !mobilesearch) {
            /**
             * sort is removed in onSearchListings func
             */
            const originMaybe = /* sort ? {} : */ { origin: viewportCenter };

            const searchParams = {
                page: 1,
                ...validFilterParams(rest, filters),
                ...searchInURL,
                address: 'Kartenbereich',
                ...originMaybe,
                ...mapViewMaybe,
                bounds: viewportBounds,
                mapSearch: true,
            };
            /**
             * no radius/distance param on moving map
             */
            delete searchParams.distance;

            history.push(
                createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams)
            );
        }
    };

    const handleSubmit = values => {
        const { search, selectedPlace } = values.location;
        const { origin, bounds } = selectedPlace;

        const searchParams = {
            ...urlQueryParams,
            address: search,
            bounds,
            origin,
        };

        history.push(
            createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams)
        );
    };

    useEffect(() => {
        /**
         * add Y spacing to and reduce the height of the map in order
         * to prevent overlapping with topbar, email confirmation bar and fitlers
         */

        /**
         * wait until email notification is made
         * visible/hidden
         * on map modal
         * closing/opening
         *
         * see applyVerticalSpacing for SF
         */
        if (isMobileLayout) {
            setMapVerticalSpacingAdjusted(true);
        }

        if (computing || isMobileLayout) return;

        setTimeout(() => {
            applyVerticalSpacing();
            setMapVerticalSpacingAdjusted(true);
        }, 0);
    }, [isMobileLayout, computing, mapPanelOpen]);

    const scrollingDisabled = Boolean(scrollingDisabledProp || (isMobileLayout && mapPanelOpen));

    useEffect(() => {
        const body = document.querySelector('body');
        const action = scrollingDisabled ? 'add' : 'remove';

        if (!body) return;
        /** see marketplaceIndex.css */
        body.classList[action]('scrollingDisabled');

        return () => {
            body.classList.remove('scrollingDisabled');
        };
    }, [scrollingDisabled]);

    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.sortSearchByDistance)

    const urlQueryParams = pickSearchParamsOnly(searchInURL, filters);

    // Page transition might initially use values from previous search
    // const urlQueryString = stringify(urlQueryParams);
    // const paramsQueryString = stringify(pickSearchParamsOnly(searchParams, filters));
    // const searchParamsAreInSync = urlQueryString === paramsQueryString;
    const validQueryParams = validURLParamsForExtendedData(
        { ...searchInURL, ...mapViewMaybe, ...distanceMaybe },
        filters
    );

    const { title, description, schema } = createSearchResultSchema(address, intl);

    const showTopbarWhenMapOpen = isMobileLayout ? !mapPanelOpen : true;

    const applyHeightToMobileMap = () => {
        if (typeof document !== 'object') return {};

        return { height: `${window.innerHeight - 72}px` };
    };

    const isMapSearch = address === 'Kartenbereich';
    const applyNoIndexTag = isMapSearch
        ? [
              {
                  name: 'robots',
                  content: 'noindex',
              },
          ]
        : [];

    const showMapForMobileDevices = isMobileLayout ? Boolean(mapViewQueryParam) : true;

    return (
        <Page
            scrollingDisabled={scrollingDisabled}
            description={description}
            title={title}
            schema={schema}
            className={css.page}
            metaTags={applyNoIndexTag}
        >
            {!isLandingPage && (
                <LayoutWrapperTopbar>
                    <TopbarContainer
                        className={classNames(css.topbar, {
                            [css.topbarHidden]: !showTopbarWhenMapOpen,
                        })}
                        desktopClassName={css.topbarDesktop}
                        currentPage="SearchPage"
                        currentSearchParams={urlQueryParams}
                        rawSearchParams={searchInURL}
                        address={address}
                        showEmailNotificationBar={!mapPanelOpen}
                    />
                </LayoutWrapperTopbar>
            )}
            <div className={css.container}>
                <MainPanel
                    urlQueryParams={validQueryParams}
                    updateUserProfileInfoInProgress={updateInfoInProgress}
                    updateUserProfileInfoError={updateInfoError}
                    mapPanelOpen={mapPanelOpen}
                    primaryFilters={filters}
                    address={address}
                    handleSubmit={handleSubmit}
                    onUpdateUserProfileInfo={onUpdateUserProfileInfo}
                    isLandingPage={isLandingPage}
                    onMapIconClick={() => {
                        navigateToSearchPage({ mapView: true });
                        setMapPanelOpen(true);
                    }}
                />
                {computing && <div className={css.placeholder} />}
                {!computing && !isLandingPage && showMapForMobileDevices && (
                    <div
                        className={classNames(css.mapWrapper, {
                            [css.mapPanelOpen]: mapPanelOpen,
                        })}
                        style={applyHeightToMobileMap()}
                    >
                        <SearchMap
                            reusableContainerClassName={css.map}
                            bounds={bounds}
                            mapVerticalSpacingAdjusted={
                                isMobileLayout || mapVerticalSpacingAdjusted
                            }
                            center={origin}
                            location={location}
                            onMapMoveEnd={onMapMoveEnd}
                            mapPanelOpen={mapPanelOpen}
                            onMapPanelToggle={flag => {
                                navigateToSearchPage(flag ? { mapView: flag } : {});
                                setMapPanelOpen(flag);
                                setMapFullScreenViewed(flag);
                            }}
                            mapFullScreenViewed={mapFullScreenViewed}
                            setMapFullScreenViewed={setMapFullScreenViewed}
                            filters={filters}
                            history={history}
                        />
                    </div>
                )}
            </div>
        </Page>
    );
    /* eslint-enable jsx-a11y/no-static-element-interactions */
};

const mapStateToProps = state => {
    const { updateUserProfileInfoInProgress, updateUserProfileInfoError } = state.user;
    const { activeListingId, searchMapListingIds, mapBounds } = state.SearchPage;

    return {
        scrollingDisabled: isScrollingDisabled(state),
        updateInfoInProgress: updateUserProfileInfoInProgress,
        updateInfoError: updateUserProfileInfoError,
        currentUser: checkMarketplaceCurrentUser(state),
        activeListingId,
        mapBoundsString: mapBounds ? encodeLatLngBounds(mapBounds) : null,
        searchMapListingIdsStr: (searchMapListingIds || [])
            .map(s => s.uuid)
            .sort()
            .join(','),
    };
};

const mapDispatchToProps = dispatch => ({
    onMapBoundsSetting: bounds => dispatch(setMapBounds(bounds)),
    onDiscardingVisitedListings: () => dispatch(discardVisitedListings()),
    onUpdateUserProfileInfo: params => dispatch(updateUserProfileInfo(params)),
    onCombineSearchMapListings: () => dispatch(combineSearchMapListings()),
    onSettingListingScoresBunch: scoreDataBunch => dispatch(setListingScores(scoreDataBunch)),
    onSearchMapListings: (params = {}, search = '') => {
        const queryParams = parse(search, {
            latlng: ['origin'],
            latlngBounds: ['bounds'],
        });

        const { page = 1, address, origin, listingType, bounds, ...rest } = queryParams;
        // const perPage = mapView ? 100 : RESULT_PAGE_SIZE;
        const originMaybe = { origin };

        dispatch(
            searchMapListings(
                {
                    ...rest,
                    ...params,
                    ...originMaybe,
                    // page: 1,
                    // perPage: 100,
                    pub_type: listingType || listingTypeHorse,
                    ...defaultSearchParams,
                },
                {
                    collectAll: true,
                }
            )
        );
    },
    onSearchListings: (params = {}, search = '') => {
        const queryParams = parse(search, {
            latlng: ['origin'],
            latlngBounds: ['bounds'],
        });

        const {
            page = 1,
            address: originAddress,
            origin,
            listingType,
            mapView,
            ...rest
        } = queryParams;
        const originMaybe = { origin };

        return dispatch(
            searchListings(
                {
                    ...rest,
                    ...params,
                    ...originMaybe,
                    page,
                    perPage: RESULT_PAGE_SIZE,
                    pub_type: listingType || listingTypeHorse,
                    ...defaultSearchParams,
                },
                { initiator: 'SearchPage', originAddress },
                /**
                 * without url bounds (a bare /s) the bounds come from the map viewport,
                 * so the url does not describe the query and is not cached
                 */
                { cacheUrl: queryParams.bounds ? search : null }
            )
        );
    },
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
)(SearchPageComponent);

export default SearchPage;
